import { RenamePrompt } from "@/components";
import { Card, CardsGrid } from "@/components/ui/Cards/Card";
import { Action, DropdownMenuVariant } from "@/components/DropdownMenu";
import { DuplicateToWorkspaceModal } from "@/components/DuplicateToWorkspaceModal";
import { MoveToFolderModal } from "@/components/MoveToFolderModal";
import { Prompt } from "@/components/ui/icons";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useDeletePromptTemplate, useEditPromptTemplate } from "@/queries";
import { PromptRegistry } from "@/types/prompt-registry";
import { displayToast } from "@/utils/toast";
import {
  ClockIcon,
  DocumentDuplicateIcon,
  DuplicateIcon,
  EmojiSadIcon,
  FolderIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { BranchToWorkspaceModal } from "../BranchToWorkspaceModal";
import { DeleteItem } from "../DeleteItem";

interface TemplateGridProps {
  templates: Array<PromptRegistry>;
}

export default function TemplateGrid({ templates }: TemplateGridProps) {
  const [isDeleteTemplateModalOpen, setDeleteTemplateModalOpen] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<null | PromptRegistry>(null);
  const auth = useAuth();
  const userContext = useUser();
  const [isMoveToFolderModalOpen, setMoveToFolderModalOpen] = useState(false);
  const [isDuplicateToWorkspaceModalOpen, setDuplicateToWorkspaceModalOpen] =
    useState(false);
  const [isBranchModalOpen, setBranchModalOpen] = useState(false);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [selectedPromptTemplate, setSelectedPromptTemplate] =
    useState<null | PromptRegistry>(null);
  const editPromptTemplate = useEditPromptTemplate();
  const deletePrompt = useDeletePromptTemplate(auth?.userToken!);

  const handleFolderMoveClick = (prompt?: PromptRegistry) => {
    setSelectedPromptTemplate(prompt || null);
    setMoveToFolderModalOpen(true);
  };

  const handleDuplicateToWorkspaceClick = (prompt?: PromptRegistry) => {
    setSelectedPromptTemplate(prompt || null);
    setDuplicateToWorkspaceModalOpen(true);
  };

  const handleBranchToWorkspaceClick = (prompt?: PromptRegistry) => {
    setSelectedPromptTemplate(prompt || null);
    setBranchModalOpen(true);
  };

  const handleRenameClick = (prompt?: PromptRegistry) => {
    setSelectedPromptTemplate(prompt || null);
    setRenameModalOpen(true);
  };

  const handleRenamePrompt = (name: string) => {
    if (selectedPromptTemplate) {
      editPromptTemplate.mutate({
        prompt_id: selectedPromptTemplate.id,
        activeWorkspaceId: userContext?.activeWorkspaceId!,
        userToken: auth?.userToken!,
        basePromptTemplate: {
          prompt_name: name,
          tags: selectedPromptTemplate.tags,
        },
      });
    }
    setRenameModalOpen(false);
  };

  const handleDeleteClick = (template?: PromptRegistry) => {
    setDeleteTemplateModalOpen(true);
    setSelectedTemplate(template || null);
  };

  const handleDelete = async () => {
    try {
      await deletePrompt.mutateAsync(selectedTemplate!);
      displayToast("Template deleted", ToastType.success);
      setDeleteTemplateModalOpen(false);
    } catch (error: unknown) {
      console.log(error);
    }
  };

  const actions: Action<PromptRegistry>[] = useMemo(
    () => [
      {
        id: "move-to-folder",
        label: "Move to folder",
        variant: DropdownMenuVariant.PRIMARY,
        icon: FolderIcon,
        onClick: handleFolderMoveClick,
      },
      {
        id: "duplicate",
        label: "Duplicate",
        variant: DropdownMenuVariant.PRIMARY,
        icon: DocumentDuplicateIcon,
        onClick: handleDuplicateToWorkspaceClick,
      },
      {
        id: "copy-a-version",
        label: "Copy a version",
        variant: DropdownMenuVariant.PRIMARY,
        icon: DuplicateIcon,
        onClick: handleBranchToWorkspaceClick,
      },
      {
        id: "rename",
        label: "Rename",
        variant: DropdownMenuVariant.PRIMARY,
        icon: PencilIcon,
        onClick: handleRenameClick,
      },
      {
        id: "delete",
        label: "Delete",
        variant: DropdownMenuVariant.DESTRUCTIVE,
        icon: TrashIcon,
        onClick: handleDeleteClick,
      },
    ],
    [],
  );

  const getMetadata = useCallback(
    (prompt: PromptRegistry) => [
      {
        text: moment(prompt.last_version_created_at).fromNow(),
        icon: ClockIcon,
        className: "text-gray-400",
      },
    ],
    [],
  );

  if (templates.length === 0) {
    return (
      <div className="mt-12 flex h-full flex-col items-center justify-center">
        <div className="text-2xl text-gray-500">
          No prompts found{" "}
          <EmojiSadIcon className="-mt-1 inline h-6 w-auto text-gray-500" />
        </div>
        <div className="text-md pt-3 text-gray-500">
          Learn more about the Prompt Registry on our{" "}
          <a
            className="text-blue-500 hover:text-blue-400"
            href="https://docs.promptlayer.com/features/prompt-registry"
          >
            docs
          </a>
          .
        </div>
      </div>
    );
  }

  return (
    <>
      <CardsGrid className="mt-4">
        {templates.map((prompt) => (
          <Card
            shouldShowEmptyText
            key={`prompt-template-${prompt.id}`}
            item={prompt}
            link={`/workspace/${userContext?.activeWorkspaceId}/prompt/${prompt.id}`}
            title={prompt.prompt_name}
            icon={<Prompt className="h-5 w-5 text-gray-400" />}
            body={
              <div className="flex min-h-[2.5rem] flex-col justify-start overflow-hidden">
                <p className="line-clamp-2 text-sm font-normal text-gray-500">
                  {prompt.description}
                </p>
              </div>
            }
            metadata={getMetadata(prompt)}
            tags={prompt.tags}
            actions={actions}
          />
        ))}
      </CardsGrid>
      {isDeleteTemplateModalOpen && selectedTemplate && (
        <DeleteItem
          open={isDeleteTemplateModalOpen}
          setOpen={setDeleteTemplateModalOpen}
          setDelete={() => handleDelete()}
          item={selectedTemplate.prompt_name}
          itemType={"template"}
        />
      )}
      {isMoveToFolderModalOpen && (
        <MoveToFolderModal
          open={isMoveToFolderModalOpen}
          prompt={selectedPromptTemplate}
          setOpen={setMoveToFolderModalOpen}
        />
      )}
      {isDuplicateToWorkspaceModalOpen && (
        <DuplicateToWorkspaceModal
          open={isDuplicateToWorkspaceModalOpen}
          prompt={selectedPromptTemplate}
          setOpen={setDuplicateToWorkspaceModalOpen}
        />
      )}
      {isBranchModalOpen && !!selectedPromptTemplate ? (
        <BranchToWorkspaceModal
          open={isBranchModalOpen}
          prompt={selectedPromptTemplate}
          setOpen={setBranchModalOpen}
        />
      ) : null}
      {selectedPromptTemplate ? (
        <RenamePrompt
          name={selectedPromptTemplate?.prompt_name || ""}
          open={isRenameModalOpen}
          setOpen={setRenameModalOpen}
          onSubmit={handleRenamePrompt}
        />
      ) : null}
    </>
  );
}
